import React, { useEffect, useMemo, useState } from 'react';

// helpers
import styled from 'styled-components';
import useTranslation from 'hooks/useTranslation';
import { StateModel } from 'redux/reducers';
import { securityAPI } from 'api/profile/securityAPI';
import { setSessionDuration } from 'redux/actions/auth';
import { useDispatch, useSelector } from 'react-redux';

// components
import { CardWrapper } from 'components/Additional/CardWrapper';
import { Text, Message, Modal, Select, SelectOption } from '@ui';

const SessionSettings = () => {
  const { t } = useTranslation(['profile', 'common']);

  const [currentSessionTimeout, setCurrentSessionTimeout] = useState(0);
  const dispatch = useDispatch();

  const profileSessionDuration = useSelector<StateModel, number>(
    (state) => state.auth.profileData?.sessionDuration || 0,
  );

  useEffect(() => {
    setCurrentSessionTimeout(profileSessionDuration || 60 * 15);
  }, [profileSessionDuration]);

  const options = useMemo<SelectOption[]>(
    () => [
      {
        label: `1 ${t('security.sessions.session_settings.minute')}`,
        id: 60,
      },
      {
        label: `5 ${t('security.sessions.session_settings.minutes')}`,
        id: 60 * 5,
      },
      {
        label: `10 ${t('security.sessions.session_settings.minutes')}`,
        id: 60 * 10,
      },
      {
        label: `15 ${t('security.sessions.session_settings.minutes')}`,
        id: 60 * 15,
      },
      {
        label: `30 ${t('security.sessions.session_settings.minutes')}`,
        id: 60 * 30,
      },
      {
        label: `45 ${t('security.sessions.session_settings.minutes')}`,
        id: 60 * 45,
      },
      {
        label: `1 ${t('security.sessions.session_settings.hour')}`,
        id: 60 * 60,
      },
    ],
    [t],
  );

  const handleTimeoutChange = (value: any) => {
    if (value === currentSessionTimeout) {
      return;
    }

    const selectedOptionTitle = options.find((e) => e.id === value)?.label;

    Modal.confirm({
      content: t(
        'security.sessions.session_settings.change_session.confirmation_message',
        { session: selectedOptionTitle },
      ),
      okText: t('yes', { ns: 'common' }),
      cancelText: t('no', { ns: 'common' }),
      onOk: () => changeSessionTimeout(value),
    });
  };

  const changeSessionTimeout = async (newTimeout: number) => {
    await securityAPI.setSessionDuration(newTimeout);
    setCurrentSessionTimeout(newTimeout);
    dispatch(setSessionDuration(newTimeout));
    Message.success(
      t(
        'security.sessions.session_settings.change_session.success_change_message',
      ),
    );
  };

  return (
    <CardWrapper>
      <Text variant="h4" weight="semi-bold">
        {t('security.sessions.session_settings.title')}
      </Text>
      <Text variant="body1">
        {t('security.sessions.session_settings.logout_after_of_inactivity')}
        <StyledSelect
          allowClear={false}
          size="small"
          options={options}
          value={currentSessionTimeout}
          onSelect={handleTimeoutChange}
        />
        {`, ${t('security.sessions.session_settings.of_inactivity')}.`}
      </Text>
    </CardWrapper>
  );
};

const StyledSelect = styled(Select)`
  width: 150px;
`;

export default SessionSettings;
